import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import dynamic from 'next/dynamic';
import { InView } from 'react-intersection-observer';
import { setError } from '../../../actions';
import Layout from '../../Ad/Layout';
import AdLabelPlaceholder from '../../Placeholders/AdLabelPlaceholder';
import HeaderPlaceholder from '../../Placeholders/HeaderPlaceholder';
import BreadcrumbsPlaceholder from '../../Placeholders/BreadcrumbsPlaceholder';
import CurrentItemGalleryPlaceholder from '../../Placeholders/CurrentItemGalleryPlaceholder';
import ItemCardPlaceholder from '../../Placeholders/ItemCardPlaceholder';
import SectionPlaceholder from '../../Placeholders/SectionPlaceholder';
import WhiteHeader from '../../WhiteHeader';
import HeaderSection from '../../Ad/Sections/HeaderSection';
import getItemTitle from '../../../utils/helpers/getCurrentItemDescriptionTitle';
import { selectRestoreAlertModalState } from '../../../store/selectors';
import useTogglePromotionModalView from '../../../hooks/useTogglePromotionModalView';
import { getAgencyDataShortQuery } from '../../../queries';
import useCurrentItemData from '../../../hooks/useCurrentItemData';
import useSuccessPromotionAlertModal from '../../../hooks/useSuccessPromotionAlertModal';
import { useBridgeState } from '../../../bridge';

const mapDispatchToProps = dispatch => {
  return {
    setErrorUI: errorState => dispatch(setError(errorState))
  };
};

const LoadingPlaceholder = () => (
  <>
    <HeaderPlaceholder />
    <BreadcrumbsPlaceholder />
    <CurrentItemGalleryPlaceholder />
    <AdLabelPlaceholder />
  </>
);

const Banner = dynamic(() => import('../../Banner'), {
  ssr: false
});
const AdManagerBanner = dynamic(() => import('../../Banner/AdManagerBanner'), {
  ssr: false
});

const CreditCalcModalView = dynamic(() => import('../../CreditCalcModalView'), {
  ssr: false
});

const Sections = dynamic(
  () => import('../../Ad/Sections/CurrentItemSections'),
  {
    loading: LoadingPlaceholder,
    ssr: false
  }
);

const SimilarAdsSection = dynamic(
  () => import('../../Ad/Sections/SimilarAdsSection'),
  {
    ssr: false
  }
);

const AgencyFilter = dynamic(() => import('../../Agency/AgencyFilter'), {
  ssr: false
});

const AlertModal = dynamic(() => import('../../AlertModal'), {
  ssr: false
});

const ValidatedSection = dynamic(
  () => import('../../Ad/Sections/ValidatedSection'),
  {
    ssr: false
  }
);

const CurrentItemPage = ({ setErrorUI }) => {
  const router = useRouter();
  const { id } = router.query;
  const { t } = useTranslation();
  const [isFavorited, setFavorited] = useState(false);
  const [isOpenCreditCalc, setOpenCreditCalc] = useState(false);
  const [skipRequest, setSkipRequest] = useState(true);
  const { isWebView } = useBridgeState();

  const { isVisible: isOpenRestoreAlertModal } = useSelector(
    selectRestoreAlertModalState
  );
  const {
    isOpenSuccessAlertModal,
    onAcceptSuccessPaymentModalTerms,
    successAlertModalIcon,
    successAlertModalTitle,
    successAlertModalMessage
  } = useSuccessPromotionAlertModal();

  const {
    data,
    item,
    loading,
    error,
    maskedPhones,
    isEditAvailable,
    isRemoveAvailable,
    isAuthor,
    isResidence,
    isAgency,
    isPublished,
    isLimited,
    isExpired,
    isRejected,
    isPending,
    isExpiredManually,
    paidServices,
    itemPath,
    prettyPrice,
    prettyArea,
    prettyAreaWithUnits,
    prettyLandAreaWithUnits,
    showElectronicBanners,
    phonesSectionNoBottomSpace,
    getPriceCurrency,
    isValidated,
    isLeased
  } = useCurrentItemData(id, router.asPath);

  const {
    onOpenRestoreViewHandler,
    onOpenLimitPaymentViewHandler,
    onAcceptRestorePaymentHandleClick,
    onDeclineRestorePayment
  } = useTogglePromotionModalView(item || {});

  const { data: agencyData, loading: agencyDataLoading } = useQuery(
    getAgencyDataShortQuery(data?.item?.business?.id),
    {
      variables: {
        id: data?.item?.business?.id
      },
      skip:
        !data ||
        !data?.item?.business?.id ||
        data?.item?.company?.targetType !== 'AGENCY' ||
        skipRequest,
      ssr: false
    }
  );

  const itemTitleData = {
    lease: item?.leased,
    area: prettyArea,
    rooms: item?.rooms,
    areaCurrency: item?.area?.units,
    category: item?.category?.name,
    location: item?.location,
    path: item?.city?.path
  };

  const calcModalDescription = getItemTitle({
    ...itemTitleData,
    isClearString: true,
    simpleData: true
  });

  useEffect(() => {
    setFavorited(item?.isBookmarked);
  }, [item?.isBookmarked]);

  useEffect(() => {
    if (error) setErrorUI(true);
  }, [error]);

  if (loading) return <LoadingPlaceholder />;

  if (isValidated) {
    return (
      <>
        <WhiteHeader
          {...{
            isFixedHeader: isWebView,
            isShowBackButton: true
          }}
        />
        <ValidatedSection {...{ item }} />
      </>
    );
  }

  return (
    <Layout
      header={
        <HeaderSection
          isButtonsVisible={isPublished}
          setIsBookmarked
          isBookmarked={isFavorited}
          setFavorited={setFavorited}
          isFixedHeader={isWebView}
        />
      }
      isCurrentItem
      currentItem={item}
    >
      <CreditCalcModalView
        summatyTitle={calcModalDescription}
        price={item?.price?.value}
        itemId={item?.id}
        initialBillOfSaleValue={item?.hasBillOfSale}
        {...{
          isOpenCreditCalc,
          setOpenCreditCalc
        }}
      />

      <Sections
        {...{
          data,
          item,
          maskedPhones,
          isEditAvailable,
          isRemoveAvailable,
          isAuthor,
          isResidence,
          isAgency,
          isPublished,
          isLimited,
          isExpired,
          isRejected,
          isPending,
          isLeased,
          isExpiredManually,
          paidServices,
          itemPath,
          prettyPrice,
          prettyArea,
          prettyAreaWithUnits,
          prettyLandAreaWithUnits,
          showElectronicBanners,
          phonesSectionNoBottomSpace,
          getPriceCurrency,
          isOpenCreditCalc,
          setOpenCreditCalc,
          onOpenRestoreViewHandler,
          onOpenLimitPaymentViewHandler,
          isFavorited,
          setFavorited
        }}
      />

      <Banner
        containerClassName={'lotriver-bottom-banner'}
        containerId={'js-lotriver-bottom-banner'}
        sid={'220686'}
        bn={'6'}
        arCn={3}
        currentItem={item}
      />
      <AdManagerBanner bannerName={'bottom'} />
      <InView
        as="div"
        onChange={inView => {
          if (inView) {
            setSkipRequest(false);
          }
        }}
      >
        {item && isAgency ? (
          <div className="container">
            {agencyDataLoading && !agencyData ? (
              <SectionPlaceholder>
                <ItemCardPlaceholder />
                <ItemCardPlaceholder />
              </SectionPlaceholder>
            ) : (
              <AgencyFilter
                categories={agencyData?.agency?.itemCategories}
                allCategories={agencyData?.categories}
                agencyId={agencyData?.agency?.id}
              />
            )}
          </div>
        ) : (
          <SimilarAdsSection
            isResidence={isResidence}
            categoryId={item?.category?.id}
            leased={item?.leased}
            companyId={item?.company?.id}
            currentItemId={item?.id}
            skipRequest={skipRequest}
          />
        )}
      </InView>
      <AlertModal
        isOpen={isOpenRestoreAlertModal}
        title={t('pages.current_item.promotion.restore_alert_title')}
        message={t('pages.current_item.promotion.restore_alert_desc')}
        acceptButtonTitle={t('pages.current_item.buttons.restore')}
        declineButtonTitle={t('common.cancel')}
        closeHandler={onDeclineRestorePayment}
        onAcceptHandlerClick={onAcceptRestorePaymentHandleClick}
        onDeclineHandlerClick={onDeclineRestorePayment}
      />
      <AlertModal
        icon={successAlertModalIcon}
        isOpen={isOpenSuccessAlertModal}
        title={successAlertModalTitle}
        message={successAlertModalMessage}
        acceptButtonTitle={t('pages.current_item.buttons.accepted')}
        closeHandler={onAcceptSuccessPaymentModalTerms}
        onAcceptHandlerClick={onAcceptSuccessPaymentModalTerms}
      />
    </Layout>
  );
};

CurrentItemPage.propTypes = {
  setErrorUI: PropTypes.func
};

export default connect(null, mapDispatchToProps)(CurrentItemPage);
